import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, ReplaySubject} from 'rxjs';
import {Profile} from '../interface/profile';
import {first, take} from 'rxjs/operators';
import {DOCUMENT} from '@angular/common';
import {UserApplicationPermission, UserApplications, UserPermissions} from '../interface/user-permissions';
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {ConfirmDialogComponent} from "../components/confirm-dialog/confirm-dialog.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {LoginLockTimerComponent} from "../components/login-lock-timer/login-lock-timer.component";
import {el} from "@fullcalendar/core/internal-common";
import {LoginLockDialogComponent} from "../components/login-lock-dialog/login-lock-dialog.component";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly profileSubject: ReplaySubject<Profile> = new ReplaySubject<Profile>(1);
  private readonly permissionSubject: ReplaySubject<UserPermissions> = new ReplaySubject<UserPermissions>(1);
  private readonly applicationsSubject: ReplaySubject<UserApplications> = new ReplaySubject<UserApplications>(1);
  private readonly userApplicationPermission: ReplaySubject<UserApplicationPermission> = new ReplaySubject<UserApplicationPermission>(1);

  private loginLockDialogRef: MatDialogRef<LoginLockTimerComponent> | null = null;

  constructor(
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document,
    private dialog: MatDialog,
    private snack: MatSnackBar,
  ) {
    this.permissionSubject.next({
      partner: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        regional_change: false,
        admin_root: false,
      },
      facility: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      contract: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      department: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      document: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      insurance_company: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      ofwca: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        regional_change: false,
        admin_root: false,
      },
      template: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      user: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      krk: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      registration: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        id_import: false,
        admin_root: false,
      },
      deregistration: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      meeting: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        read_and_sign_up: false,
        admin_root: false,
      },
      idd_training: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        read_and_sign_up: false,
        admin_root: false,
      },
      sold_policy: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        read_and_sign_up: false,
        policy_import: false,
        admin_root: false,
      },
      settlement: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        margin: false,
        admin_root: false,
      },
      commission_table: {
        read: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      commission_redirection: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        import: false,
        admin_root: false,
      },
      newsletter: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        import: false,
        admin_root: false,
      },
      licensed_agent: {
        read: false,
        status_editing: false,
        admin_root: false,
      },
      support: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      analysis: {
        read_report: false,
        admin_root: false,
      },
      partner_search_engine: {
        read: false,
        admin_root: false,
      },
      employees: {
        read: false,
        read_report: false,
        admin_root: false,
      },
      block_ofwca: {
        delete: false,
        admin_root: false,
      },
      corporation: {
        read: false,
        read_report: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      asf_360: {
        read: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      address_database: {
        read: false,
        create: false,
        admin_root: false,
      },
      ock: {
        create: false,
        admin_root: false,
      },
      ofwca_access_report: {
        read_report: false,
        admin_root: false,
    },
      form: {
        read: false,
        read_report: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      receivables: {
        limited_read: false,
        read: false,
        read_report: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      mailing: {
        read: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      partner_additional: {
        read_report: false,

      },
      ofwca_additional: {
        read_report: false,

      },
      idd_training_additional: {
        read_report: false,

      },
      registration_additional: {
        read_report: false,

      },
      note: {
        read: false,
        admin_root: false,
      },
      terminals: {
        read: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      file_manager: {
        read: false,
        create: false,
        delete: false,
        admin_root: false,
      },
      personal_data_change: {
        read: false,
        create: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      file_sharing: {
        read: false,
        create: false,
        delete: false,
        admin_root: false,
      },
      chronie: {
        read: false,
        read_report: false,
        admin_root: false,
      },
      human_resources: {
        read: false,
        read_report: false,
        write: false,
        admin_root: false,
      },
      license: {
        read: false,
        read_report: false,
        write: false,
        admin_root: false,
      },
      marketing: {
        read: false,
        read_report: false,
        write: false,
        delete: false,
        admin_root: false,
      },
      dobry_agent: {
        read: false,
        write: false,
        admin_root: false,
      },
      dynamic_dictionary: {
        read: false,
        write: false,
        admin_root: false,
      },
      tag_import: {
        read: false,
        write: false,
        admin_root: false,
      },
      hiperio: {
        limited_read: false,
        read: false,
        read_report: false,
        write: false,
        admin_root: false,
      },
      import_contacts: {
        read: false,
        write: false,
        delete: false,
        admin_root: false,
      }
    });

    this.applicationsSubject.next({
      asf360: {
        active: false,
      },
      asf24: {
        active: false,
      },
      apk: {
        active: false,
      },
      chronie: {
        active: false,
      }
    });
    this.userApplicationPermission.next({
      dobryagent: {
        active: false,
        blog: false,
        business_card: false,
      },
      apk: {
        active: false,
      },
    });
    this.loadProfile();
  }

  public loadProfile(): void {
    this.http.get('/rest/sso/oauth/profile')
      .pipe(take(1))
      .subscribe((response: Profile) => {
        // if (response.loginLock)
        // {
        //   this.snack.openFromComponent(LoginLockTimerComponent);
        // }
        response.roles.forEach((role) => {
          if (role === 'ROLE_ASF360'){
            this.document.location.href = '/sso';
          }
        });
        this.profileSubject.next(response);
        this.calculatePermissions(response);
        this.calculateApplications(response);
        this.calculateUserApplicationPermission(response);
        this.document.body.classList.add('asf-splash-screen-hidden');
        if (response.loginLock)
        {
          if (response.loginLockMessage === false){
            const parseDate = (dateStr: string) => new Date(dateStr.replace(' ', 'T')).getTime();

            const lockEnd = parseDate(response.loginLockEnd);
            const lockStart = parseDate(response.loginLock);
            const diffInMinutes = Math.floor((lockEnd - lockStart) / (1000 * 60));

            this.dialog.open(LoginLockDialogComponent, {
              width: '500px',
              data:
                {
                  startAt: 10,
                  endAt: diffInMinutes,
                  loginLock: response.loginLock
                }
            });
          }
          // this.snack.openFromComponent(LoginLockTimerComponent);
          if (!this.loginLockDialogRef) {
              this.loginLockDialogRef = this.dialog.open(LoginLockTimerComponent, {
                disableClose: true,       // Użytkownik nie zamknie dialogu klikając obok ani Esc
                hasBackdrop: false,       // Brak ciemnego tła, użytkownik widzi resztę aplikacji
                position: {
                  bottom: '24px',         // Pozycjonowanie identyczne jak snackbar
                }
              });
            this.loginLockDialogRef.afterClosed().subscribe(() => {
              this.loginLockDialogRef = null;
            });
          }
        } else {
          if (this.loginLockDialogRef) {
            this.loginLockDialogRef.close();
            this.loginLockDialogRef = null;
          }
        }
      })
    ;
  }

  public getProfile(): ReplaySubject<Profile> {
    return this.profileSubject;
  }

  public getUserPermissions(): ReplaySubject<UserPermissions> {
    return this.permissionSubject;
  }

  public getUserApplications(): ReplaySubject<UserApplications> {
    return this.applicationsSubject;
  }
  public getUserApplicationPermission(): ReplaySubject<UserApplicationPermission> {
    return this.userApplicationPermission;
  }

  public logout(): void {
    this.http.post('/rest/sso/logout', '')
      .pipe(take(1))
      .subscribe(() => {
        this.document.location.href = '/sso/';
      });
  }

  public getGoogleAuthQrCode(): Observable<{ url: string }> {
    return this.http.get(`/rest/sso/api/ga-qrcode`)
      .pipe(first()) as Observable<{ url: string }>;
  }

  public enableGoogleAuthenticator(formData: any): Observable<any> {
    return this.http.post(`/rest/sso/api/ga-enable`, formData)
      .pipe(first());
  }

  private calculatePermissions(profile: Profile): void {
    const permissions: UserPermissions = {
      partner: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'regional_change', 'admin_root'].includes(profile.permissions.partner),
        read_report: ['read_report', 'create', 'write', 'full', 'regional_change', 'admin_root'].includes(profile.permissions.partner)
          && !profile.reportPermissions.includes('partner'),
        create: ['create', 'write', 'full', 'regional_change', 'admin_root'].includes(profile.permissions.partner),
        write: ['write', 'full', 'regional_change', 'admin_root'].includes(profile.permissions.partner),
        delete: ['full', 'regional_change', 'admin_root'].includes(profile.permissions.partner),
        regional_change: ['regional_change', 'admin_root'].includes(profile.permissions.partner),
        admin_root: ['admin_root'].includes(profile.permissions.partner)
      },
      facility: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.facility),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.facility)
          && !profile.reportPermissions.includes('facility'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.facility),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.facility),
        delete: ['full', 'admin_root'].includes(profile.permissions.facility),
        admin_root: ['admin_root'].includes(profile.permissions.facility)
      },
      contract: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.contract),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.contract)
          && !profile.reportPermissions.includes('contract'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.contract),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.contract),
        delete: ['full', 'admin_root'].includes(profile.permissions.contract),
        admin_root: ['admin_root'].includes(profile.permissions.contract),
      },
      department: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.department),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.department)
          && !profile.reportPermissions.includes('department'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.department),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.department),
        delete: ['full', 'admin_root'].includes(profile.permissions.department),
        admin_root: ['admin_root'].includes(profile.permissions.department)
      },
      document: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.document),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.document)
          && !profile.reportPermissions.includes('document'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.document),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.document),
        delete: ['full', 'admin_root'].includes(profile.permissions.document),
        admin_root: ['admin_root'].includes(profile.permissions.document)
      },
      insurance_company: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.insurance_company),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.insurance_company)
          && !profile.reportPermissions.includes('insurance_company'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.insurance_company),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.insurance_company),
        delete: ['full', 'admin_root'].includes(profile.permissions.insurance_company),
        admin_root: ['admin_root'].includes(profile.permissions.insurance_company)
      },
      ofwca: {
        limited_read: ['regional_change', 'limited_read', 'read',
          'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.ofwca),
        read: ['regional_change', 'read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.ofwca),
        read_report: ['regional_change', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.ofwca)
          && !profile.reportPermissions.includes('ofwca'),
        create: ['regional_change', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.ofwca),
        write: ['regional_change', 'write', 'full', 'admin_root'].includes(profile.permissions.ofwca),
        delete: ['regional_change', 'full', 'admin_root'].includes(profile.permissions.ofwca),
        regional_change: ['regional_change', 'admin_root'].includes(profile.permissions.ofwca),
        admin_root: ['admin_root'].includes(profile.permissions.ofwca)
      },
      template: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.template),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.template)
          && !profile.reportPermissions.includes('template'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.template),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.template),
        delete: ['full', 'admin_root'].includes(profile.permissions.template),
        admin_root: ['admin_root'].includes(profile.permissions.template)
      },
      user: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.user),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.user)
          && !profile.reportPermissions.includes('user'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.user),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.user),
        delete: ['full', 'admin_root'].includes(profile.permissions.user),
        admin_root: ['admin_root'].includes(profile.permissions.user)
      },
      krk: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.krk),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.krk)
          && !profile.reportPermissions.includes('krk'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.krk),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.krk),
        delete: ['full', 'admin_root'].includes(profile.permissions.krk),
        admin_root: ['admin_root'].includes(profile.permissions.krk)
      },
      registration: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'id_import', 'admin_root'].includes(profile.permissions.registration),
        read_report: ['read_report', 'create', 'write', 'full', 'id_import', 'admin_root'].includes(profile.permissions.registration)
          && !profile.reportPermissions.includes('registration'),
        create: ['create', 'write', 'full', 'id_import', 'admin_root'].includes(profile.permissions.registration),
        write: ['write', 'full', 'id_import', 'admin_root'].includes(profile.permissions.registration),
        delete: ['full', 'id_import', 'admin_root'].includes(profile.permissions.registration),
        id_import: ['id_import', 'admin_root'].includes(profile.permissions.registration),
        admin_root: ['admin_root'].includes(profile.permissions.registration)
      },
      deregistration: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.deregistration),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.deregistration)
          && !profile.reportPermissions.includes('deregistration'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.deregistration),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.deregistration),
        delete: ['full', 'admin_root'].includes(profile.permissions.deregistration),
        admin_root: ['admin_root'].includes(profile.permissions.deregistration)
      },
      meeting: {
        read: ['read', 'read_and_sign_up', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.meeting),
        read_and_sign_up: ['read_and_sign_up', 'read_report', 'create', 'write', 'full',
          'admin_root'].includes(profile.permissions.meeting),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.meeting)
          && !profile.reportPermissions.includes('meeting'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.meeting),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.meeting),
        delete: ['full', 'admin_root'].includes(profile.permissions.meeting),
        admin_root: ['admin_root'].includes(profile.permissions.meeting)
      },
      idd_training: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.idd_training),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.idd_training)
          && !profile.reportPermissions.includes('idd_training'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.idd_training),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.idd_training),
        delete: ['full', 'admin_root'].includes(profile.permissions.idd_training),
        admin_root: ['admin_root'].includes(profile.permissions.idd_training)
      },
      sold_policy: {
        read: ['read', 'read_report', 'policy_import', 'admin_root'].includes(profile.permissions.sold_policy),
        read_report: ['read_report', 'policy_import', 'admin_root'].includes(profile.permissions.sold_policy)
          && !profile.reportPermissions.includes('sold_policy'),
        create: ['policy_import', 'admin_root'].includes(profile.permissions.sold_policy),
        write: ['policy_import', 'admin_root'].includes(profile.permissions.sold_policy),
        delete: ['policy_import', 'admin_root'].includes(profile.permissions.sold_policy),
        policy_import: ['policy_import', 'admin_root'].includes(profile.permissions.sold_policy),
        admin_root: ['admin_root'].includes(profile.permissions.sold_policy)
      },
      settlement: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'margin', 'admin_root'].includes(profile.permissions.settlement),
        read_report: ['read_report', 'create', 'write', 'full', 'margin', 'admin_root'].includes(profile.permissions.settlement)
          && !profile.reportPermissions.includes('settlement'),
        create: ['create', 'write', 'full', 'margin', 'admin_root'].includes(profile.permissions.settlement),
        write: ['write', 'full', 'margin', 'admin_root'].includes(profile.permissions.settlement),
        delete: ['full', 'margin', 'admin_root'].includes(profile.permissions.settlement),
        margin: ['margin', 'admin_root'].includes(profile.permissions.settlement),
        admin_root: ['admin_root'].includes(profile.permissions.settlement),
      },
      commission_table: {
        read: ['read', 'write', 'full', 'admin_root'].includes(profile.permissions.commission_table),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.commission_table),
        delete: ['full', 'admin_root'].includes(profile.permissions.commission_table),
        admin_root: ['admin_root'].includes(profile.permissions.commission_table),
      },
      commission_redirection: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'import',
          'admin_root'].includes(profile.permissions.commission_redirection),
        read_report: ['read_report', 'create', 'write', 'full', 'import', 'admin_root'].includes(profile.permissions.commission_redirection)
          && !profile.reportPermissions.includes('commission_redirection'),
        create: ['create', 'write', 'full', 'import', 'admin_root'].includes(profile.permissions.commission_redirection),
        write: ['write', 'full', 'import', 'admin_root'].includes(profile.permissions.commission_redirection),
        delete: ['full', 'import', 'admin_root'].includes(profile.permissions.commission_redirection),
        import: ['import', 'admin_root'].includes(profile.permissions.commission_redirection),
        admin_root: ['admin_root'].includes(profile.permissions.commission_redirection)
      },
      newsletter: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'import', 'admin_root'].includes(profile.permissions.newsletter),
        read_report: ['read_report', 'create', 'write', 'full', 'import', 'admin_root'].includes(profile.permissions.newsletter)
          && !profile.reportPermissions.includes('newsletter'),
        create: ['create', 'write', 'full', 'import', 'admin_root'].includes(profile.permissions.newsletter),
        write: ['write', 'full', 'import', 'admin_root'].includes(profile.permissions.newsletter),
        delete: ['full', 'import', 'admin_root'].includes(profile.permissions.newsletter),
        import: ['import', 'admin_root'].includes(profile.permissions.newsletter),
        admin_root: ['admin_root'].includes(profile.permissions.newsletter)
      },
      licensed_agent: {
        read: ['read', 'status_editing', 'admin_root'].includes(profile.permissions.licensed_agent),
        status_editing: ['status_editing', 'admin_root'].includes(profile.permissions.licensed_agent),
        admin_root: ['admin_root'].includes(profile.permissions.licensed_agent)
      },
      support: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.support),
        read_report:  ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.support)
        && !profile.reportPermissions.includes('support'),
        create:  ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.support),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.support),
        delete:  ['full', 'admin_root'].includes(profile.permissions.support),
        admin_root: ['admin_root'].includes(profile.permissions.support),
      },
      analysis: {
        read_report:  ['read_report', 'admin_root'].includes(profile.permissions.analysis),
        admin_root: ['admin_root'].includes(profile.permissions.analysis),
      },
      partner_search_engine: {
        read: ['read', 'admin_root'].includes(profile.permissions.partner_search_engine),
        admin_root: ['admin_root'].includes(profile.permissions.partner_search_engine),
      },
      employees: {
        read: ['read', 'read_report', 'admin_root'].includes(profile.permissions.employees),
        read_report:  ['read_report', 'admin_root'].includes(profile.permissions.employees)
          && !profile.reportPermissions.includes('employees'),
        admin_root: ['admin_root'].includes(profile.permissions.employees),
      },
      block_ofwca: {
        delete:  ['full', 'admin_root'].includes(profile.permissions.block_ofwca),
        admin_root: ['admin_root'].includes(profile.permissions.block_ofwca),
      },
      corporation: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.corporation),
        read_report:  ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.corporation)
          && !profile.reportPermissions.includes('corporation'),
        create:  ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.corporation),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.corporation),
        delete:  ['full', 'admin_root'].includes(profile.permissions.corporation),
        admin_root: ['admin_root'].includes(profile.permissions.corporation),
      },
      asf_360: {
        read: ['read',  'create', 'write', 'full', 'admin_root'].includes(profile.permissions.asf_360),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.asf_360),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.asf_360),
        delete: ['full', 'admin_root'].includes(profile.permissions.asf_360),
        admin_root: ['admin_root'].includes(profile.permissions.asf_360)
      },
      address_database: {
        read: ['read',  'create', 'admin_root'].includes(profile.permissions.address_database),
        create: ['create', 'admin_root'].includes(profile.permissions.address_database),
        admin_root: ['admin_root'].includes(profile.permissions.address_database)
      },
      ock: {
        create: ['create', 'full', 'admin_root'].includes(profile.permissions.ock),
        delete: ['full', 'admin_root'].includes(profile.permissions.ock),
        admin_root: ['admin_root'].includes(profile.permissions.ock)
      },
      ofwca_access_report: {
        read_report: ['read_report', 'admin_root'].includes(profile.permissions.ofwca_access_report),
        admin_root: ['admin_root'].includes(profile.permissions.ofwca_access_report)
      },
      form: {
        read: ['read' , 'read_report', 'write', 'full', 'admin_root'].includes(profile.permissions.form),
        read_report:  ['read_report', 'write', 'full', 'admin_root'].includes(profile.permissions.form),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.form),
        delete:  ['full', 'admin_root'].includes(profile.permissions.form),
        admin_root: [ 'admin_root'].includes(profile.permissions.form)
      },
      receivables: {
        limited_read:  ['limited_read', 'read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.receivables),
        read:  ['read', 'read_report', 'create',  'write', 'full', 'admin_root'].includes(profile.permissions.receivables),
        read_report:  ['read_report', 'create', 'write', 'full',  'admin_root'].includes(profile.permissions.receivables)
          && !profile.reportPermissions.includes('receivables'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.receivables),
        write:  ['write', 'full',  'admin_root'].includes(profile.permissions.receivables),
        delete:  ['full', 'admin_root'].includes(profile.permissions.receivables),
        admin_root:  [ 'admin_root'].includes(profile.permissions.receivables)
      },
      mailing: {
        read: ['read',  'create', 'write', 'full', 'admin_root'].includes(profile.permissions.mailing),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.mailing),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.mailing),
        delete: ['full', 'admin_root'].includes(profile.permissions.mailing),
        admin_root: ['admin_root'].includes(profile.permissions.mailing)
      },
      partner_additional: {
        read_report:  ['read_report'].includes(profile.permissions.partner_additional)
      },
      ofwca_additional: {
        read_report:  ['read_report'].includes(profile.permissions.ofwca_additional)
      },
      idd_training_additional: {
        read_report:  ['read_report'].includes(profile.permissions.idd_training_additional)
      },
      registration_additional: {
        read_report:  ['read_report'].includes(profile.permissions.registration_additional)
      },
      note: {
        limited_read: [ 'limited_read', 'read', 'admin_root'].includes(profile.permissions.note),
        read: [ 'read', 'admin_root'].includes(profile.permissions.note),
        admin_root: ['admin_root'].includes(profile.permissions.note)
      },
      terminals: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.terminals),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.terminals)
          && !profile.reportPermissions.includes('terminals'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.terminals),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.terminals),
        delete: ['full', 'admin_root'].includes(profile.permissions.terminals),
        admin_root: ['admin_root'].includes(profile.permissions.terminals)
      },
      file_manager: {
        read: ['read', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.file_manager),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.file_manager),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.file_manager),
        delete: ['full', 'admin_root'].includes(profile.permissions.file_manager),
        admin_root: ['admin_root'].includes(profile.permissions.file_manager)
      },
      personal_data_change: {
        read: ['read', 'read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.personal_data_change),
        read_report: ['read_report', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.personal_data_change)
          && !profile.reportPermissions.includes('personal_data_change'),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.personal_data_change),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.personal_data_change),
        delete: ['full', 'admin_root'].includes(profile.permissions.personal_data_change),
        admin_root: ['admin_root'].includes(profile.permissions.personal_data_change)
      },
      file_sharing: {
        read: ['read', 'create', 'write', 'full', 'admin_root'].includes(profile.permissions.file_sharing),
        create: ['create', 'write', 'full', 'admin_root'].includes(profile.permissions.file_sharing),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.file_sharing),
        delete: ['full', 'admin_root'].includes(profile.permissions.file_sharing),
        admin_root: ['admin_root'].includes(profile.permissions.file_sharing)
      },

      chronie: {
        read: ['read' , 'read_report', 'write', 'full', 'admin_root'].includes(profile.permissions.chronie),
        read_report:  ['read_report', 'write', 'full', 'admin_root'].includes(profile.permissions.chronie),
        admin_root: [ 'admin_root'].includes(profile.permissions.chronie),
      },
      human_resources: {
        read: ['read' , 'read_report', 'write',  'admin_root'].includes(profile.permissions.human_resources),
        read_report: ['read_report',  'write',  'admin_root'].includes(profile.permissions.human_resources),
        write: ['write', 'admin_root'].includes(profile.permissions.human_resources),
        admin_root: [ 'admin_root'].includes(profile.permissions.human_resources),
      },
      license: {
        read: ['read', 'read_report', 'write', 'admin_root'].includes(profile.permissions.license),
        read_report: ['read_report', 'write', 'admin_root'].includes(profile.permissions.license),
        write: ['write', 'admin_root'].includes(profile.permissions.license),
        admin_root: ['admin_root'].includes(profile.permissions.license)
      },
      marketing: {
        read: ['read' , 'read_report', 'write', 'full', 'admin_root'].includes(profile.permissions.marketing),
        read_report:  ['read_report', 'write', 'full', 'admin_root'].includes(profile.permissions.marketing),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.marketing),
        delete:  ['full', 'admin_root'].includes(profile.permissions.marketing),
        admin_root: [ 'admin_root'].includes(profile.permissions.marketing)
      },
      dobry_agent: {
        read: ['read' , 'read_report', 'write', 'full', 'admin_root'].includes(profile.permissions.dobry_agent),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.dobry_agent),
        admin_root: [ 'admin_root'].includes(profile.permissions.dobry_agent)
      },
      dynamic_dictionary: {
        read: ['read' , 'read_report', 'write', 'full', 'admin_root'].includes(profile.permissions.dynamic_dictionary),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.dynamic_dictionary),
        admin_root: [ 'admin_root'].includes(profile.permissions.dynamic_dictionary)
      },
      tag_import: {
        read: ['read', 'write', 'admin_root'].includes(profile.permissions.tag_import),
        write: ['write', 'admin_root'].includes(profile.permissions.tag_import),
        admin_root: [ 'admin_root'].includes(profile.permissions.tag_import)
      },
      hiperio: {
        limited_read:  ['limited_read', 'read', 'read_report', 'write', 'admin_root'].includes(profile.permissions.hiperio),
        read: ['read' , 'read_report', 'write',  'admin_root'].includes(profile.permissions.hiperio),
        read_report:  ['read_report', 'write',  'admin_root'].includes(profile.permissions.hiperio),
        write: ['write',  'admin_root'].includes(profile.permissions.hiperio),
        admin_root: [ 'admin_root'].includes(profile.permissions.hiperio)
      },
      import_contacts: {
        read: ['read',  'write', 'full', 'admin_root'].includes(profile.permissions.import_contacts),
        write: ['write', 'full', 'admin_root'].includes(profile.permissions.import_contacts),
        delete: ['full', 'admin_root'].includes(profile.permissions.import_contacts),
        admin_root: ['admin_root'].includes(profile.permissions.import_contacts)
      },
    };

    this.permissionSubject.next(permissions);
  }

  private calculateApplications(profile: Profile): void {
    const applications: UserApplications = {
      asf360: {
        active: ['active'].includes(profile.applications?.asf360),
      },
      asf24: {
        active: ['active'].includes(profile.applications?.asf24),
      },
      apk: {
        active: ['active'].includes(profile.applications?.apk),
      },
      chronie: {
        active: ['active'].includes(profile.applications?.chronie),
      },
    };

    this.applicationsSubject.next(applications);
  }

  private calculateUserApplicationPermission(profile: Profile): void {
    const applications: UserApplicationPermission = {
      dobryagent: {
        active: ['active'].includes(profile.applicationPermission?.dobryagent?.application_access),
        blog: ['active'].includes(profile.applicationPermission?.dobryagent?.blog),
        business_card: ['active'].includes(profile.applicationPermission?.dobryagent?.business_card),
      },
      apk: {
        active: ['active'].includes(profile.applicationPermission?.apk?.application_access),
      },
    };

    this.userApplicationPermission.next(applications);
  }
}
